import dayjs from 'dayjs'
import { ofetch } from 'ofetch'

const modalId = 'pd26-lot-programmatic-modal';
const toastTagName = 'pd26-toast';
const toastGlobalKey = '__pd26ToastElement';

class Pd26Toast extends HTMLElement {
  static get observedAttributes() {
    return ['message', 'open'];
  }

  static getElement() {
    if (!window[toastGlobalKey] || !window[toastGlobalKey].isConnected) {
      window[toastGlobalKey] = document.createElement(toastTagName);
      document.body.appendChild(window[toastGlobalKey]);
    }

    return window[toastGlobalKey];
  }

  static show(message, options = {}) {
    return this.getElement().show(message, options);
  }

  static hide() {
    return this.getElement().hide();
  }

  constructor() {
    super();

    this._hideTimer = 0;
    this._shadow = this.attachShadow({ mode: 'open' });
    this._style = document.createElement('style');
    this._toast = document.createElement('div');
    this._message = document.createElement('span');

    this._style.textContent = `
          :host {
            position: fixed;
            z-index: 2147483000;
            top: 152px;
            left: 50%;
            display: block;
            max-width: min(720px, calc(100vw - 32px));
            pointer-events: none;
            transform: translate(-50%, -8px);
            opacity: 0;
            transition: opacity 180ms ease, transform 180ms ease;
          }

          :host([open]) {
            transform: translate(-50%, 0);
            opacity: 1;
          }

          .pd26-toast {
            display: flex;
            align-items: center;
            justify-content: center;
            width: max-content;
            max-width: 100%;
            padding: 12px;
            border-radius: 12px;
            background: rgba(26, 26, 26, 0.85);
            color: #ffffff;
            box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
          }

          .pd26-toast__message {
            color: inherit;
            font-size: 20px;
            font-weight: 600;
            line-height: 28px;
            letter-spacing: 0;
            text-align: center;
            overflow-wrap: anywhere;
          }

          @media (max-width: 767px) {
            :host {
              top: 112px;
              max-width: calc(100vw - 32px);
            }

            .pd26-toast {
              padding: 10px 12px;
              border-radius: 12px;
            }

            .pd26-toast__message {
              font-size: 14px;
              line-height: 20px;
            }
          }

          @media (prefers-reduced-motion: reduce) {
            :host {
              transition: none;
            }
          }
        `;

    this._toast.className = 'pd26-toast';
    this._toast.setAttribute('part', 'toast');
    this._toast.setAttribute('role', 'status');
    this._toast.setAttribute('aria-live', 'polite');
    this._message.className = 'pd26-toast__message';
    this._message.setAttribute('part', 'message');

    this._toast.appendChild(this._message);
    this._shadow.append(this._style, this._toast);
  }

  connectedCallback() {
    this._render();
  }

  disconnectedCallback() {
    window.clearTimeout(this._hideTimer);
  }

  attributeChangedCallback() {
    this._render();
  }

  show(message, options = {}) {
    const duration = Number(options.duration || this.getAttribute('duration') || 3000);

    if (message !== undefined && message !== null) this.setAttribute('message', String(message));

    window.clearTimeout(this._hideTimer);
    this.setAttribute('open', '');

    if (duration > 0) {
      this._hideTimer = window.setTimeout(() => {
        this.hide();
      }, duration);
    }

    return this;
  }

  hide() {
    window.clearTimeout(this._hideTimer);
    this.removeAttribute('open');
    return this;
  }

  _render() {
    this._message.textContent = this.getAttribute('message') || '';
  }
}

if (!customElements.get(toastTagName)) {
  customElements.define(toastTagName, Pd26Toast);
}

function getModal() {
  let modal = document.getElementById(modalId);
  if (!modal) {
    modal = document.createElement('pdp-info-modal');
    modal.id = modalId;
    modal.setAttribute('close-only', '');
    document.body.appendChild(modal);
  }
  modal.setClass('pd26-lot-modal');
  return modal;
}

function buildLotContent(options = {}) {
  const state = options.state === 'prize' ? 'prize' : 'coins'
  const template = document.querySelector('[data-pd26-lot-template]') || document.querySelector(`template[data-state="${state}"]`)
  if (!template) return ''

  const fragment = template.content.cloneNode(true)
  const lotEl = fragment.querySelector('.pd26-lot')
  const titleEl = fragment.querySelector('.pd26-lot-title')
  const prizeImage = fragment.querySelector('.pd26-lot-prize img, .pd26-lot-coin')
  const copyStrong = fragment.querySelector('.pd26-lot-copy strong')
  const noteEl = fragment.querySelector('.pd26-lot-note')
  const actionEl = fragment.querySelector('.pd26-lot-action')

  if (lotEl) lotEl.classList.toggle('pd26-lot-coins', state === 'coins')
  if (titleEl && options.title) titleEl.textContent = options.title
  if (prizeImage) {
    if (options.image) {
      prizeImage.src = options.image
      prizeImage.alt = options.imageAlt || ''
      prizeImage.hidden = false
    } else {
      prizeImage.removeAttribute('src')
      prizeImage.alt = ''
      prizeImage.hidden = true
    }
  }
  if (copyStrong && options.strongText != null) copyStrong.textContent = options.strongText
  if (noteEl && options.noteText != null) noteEl.textContent = options.noteText
  if (actionEl && options.actionText) actionEl.textContent = options.actionText

  const wrapper = document.createElement('div')
  wrapper.appendChild(fragment)
  return wrapper.innerHTML
}

window.openPd26LotModal = function openPd26LotModal(options = {}) {
  const modal = getModal();
  modal.setTitle(options.title || '');
  modal.setContent(buildLotContent(options));
  modal.show();

  const action = modal._overlay && modal._overlay.querySelector('.pd26-lot-action');
  if (action && typeof options.onAction === 'function') {
    action.addEventListener('click', options.onAction, { once: true });
  }
};

const PD26_PENDING_SIGN_KEY = 'pd26:pending-sign'
const PD26_PENDING_SIGN_TTL_MS = 10 * 60 * 1000
const pd26CheckinState = {
  res: null,
  activityCode: '',
  activitySignBeanList: [],
  root: null,
  signing: false,
}

function getActivityCode() {
  return pd26CheckinState.activityCode || pd26CheckinState.res?.activityCode || ''
}

function setPendingSignAfterLogin(pending) {
  try {
    if (pending) {
      sessionStorage.setItem(PD26_PENDING_SIGN_KEY, String(Date.now()))
    } else {
      sessionStorage.removeItem(PD26_PENDING_SIGN_KEY)
    }
  } catch (_) {
    /* sessionStorage unavailable */
  }
}

function hasPendingSignAfterLogin() {
  try {
    const raw = sessionStorage.getItem(PD26_PENDING_SIGN_KEY)
    if (!raw) return false

    const ts = Number(raw)
    if (!Number.isFinite(ts) || Date.now() - ts > PD26_PENDING_SIGN_TTL_MS) {
      sessionStorage.removeItem(PD26_PENDING_SIGN_KEY)
      return false
    }

    return true
  } catch (_) {
    return false
  }
}

function clearPendingSignAfterLogin() {
  try {
    sessionStorage.removeItem(PD26_PENDING_SIGN_KEY)
  } catch (_) {
    /* sessionStorage unavailable */
  }
}

function getCheckinLoginRedirect(root) {
  const url = new URL(window.location.href)
  url.hash = root?.id || 'pd26-checkin'
  return url.toString()
}

function openLoginForCheckin() {
  const loginDialog = document.querySelector('login-dialog')
  if (!loginDialog?.open) return

  const root = pd26CheckinState.root || document.querySelector('[data-pd26-checkin]')
  const activityType = root?.dataset?.activityType

  loginDialog.open(null, {
    ...(activityType ? { activityType } : {}),
    redirect: getCheckinLoginRedirect(root),
  })
}
const cookie_key = window.__MC__CONFIG__.cookieName

function clearCookie(name, domain = window.location.hostname) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}; SameSite=Lax`
}

function clearToken() {
  clearCookie(cookie_key)
}

const PD26_TOKEN_EXPIRED_RELOAD_KEY = 'pd26:token-expired-reload'

function refreshAfterTokenExpired() {
  try {
    if (sessionStorage.getItem(PD26_TOKEN_EXPIRED_RELOAD_KEY)) return
    sessionStorage.setItem(PD26_TOKEN_EXPIRED_RELOAD_KEY, '1')
  } catch (_) {
    /* sessionStorage unavailable */
  }

  window.location.reload()
}

const ofetchInstance = ofetch.create({
  baseURL: window.__MC__CONFIG__.apiUrl,
  onRequest({ options }) {
    options.headers = new Headers({
      Authorization: `Bearer ${window.__MOMCOZY_USER_INFO__?.token || ''}`,
      ...window.__MC__CONFIG__.headers,
      ...options.headers,
    })
  },
  onResponse(res) {
    const data = res.response._data
    if (data && data.code === '200') {
      try {
        sessionStorage.removeItem(PD26_TOKEN_EXPIRED_RELOAD_KEY)
      } catch (_) {
        /* sessionStorage unavailable */
      }
      res.response._data = data.result || data.data || true
      return
    }

    if (data && data.code === '10001003') {
      clearToken()
      refreshAfterTokenExpired()
    }

    const errRes = new Response(data, { status: 417 })
    errRes._data = data
    res.response = errRes
  },
})

function getCheckinList() {
  return ofetchInstance('/api/lute/platform/activity/sign/querySignList', {
    method: 'POST',
    body: {
      platForm: 'WEB',
    },
  })
}

function doSign(body = {}) {
  return ofetchInstance('/api/lute/platform/activity/sign/sign', {
    method: 'POST',
    body: {
      platForm: 'WEB',
      ...body,
    },
  })
}

function getApiMessage(error) {
  return error?.response._data?.message || 'Unknown error'
}

function isLoggedIn() {
  return Boolean(window.__MOMCOZY_USER_INFO__?.token)
}

function getTodayItem(activitySignBeanList) {
  return activitySignBeanList.find((item) => Number(item.isNowDay) === 0)
}

/** 列表中「今天」所在下标；API 保证 isNowDay===0 为当前日，其前的项为已过去的日期 */
function getTodayListIndex(activitySignBeanList) {
  return activitySignBeanList.findIndex((item) => Number(item.isNowDay) === 0)
}

function isPastCheckinSlot(itemIndex, todayIndex) {
  return todayIndex >= 0 && itemIndex < todayIndex
}

function isPrizeReward(data) {
  return Array.isArray(data?.extraRewards) && data.extraRewards.length > 0
}

function getRewardAmountKey(amount) {
  if (amount == null || amount === '' || Number(amount) === 0) return ''

  const numericAmount = Number(amount)
  return Number.isFinite(numericAmount) ? String(numericAmount) : String(amount)
}

function getRewardImageKey(reward) {
  const amountKey = getRewardAmountKey(reward?.rewardAmount)
  if (!amountKey) return ''

  if (Number(reward?.rewardType) === 20) return `gift_card:${amountKey}`
  if (Number(reward?.rewardType) === 40) return `points:${amountKey}`

  return ''
}

function findConfiguredRewardImage(data, i18n) {
  const rewardImages = Array.isArray(i18n?.rewardImages) ? i18n.rewardImages : []
  if (!rewardImages.length) return ''

  const rewardKeys = []
  const extraPointKeys = []

  if (Array.isArray(data?.extraRewards)) {
    data.extraRewards.forEach((reward) => {
      const rewardKey = getRewardImageKey(reward)
      if (!rewardKey) return

      if (Number(reward?.rewardType) === 20) {
        rewardKeys.push(rewardKey)
      } else {
        extraPointKeys.push(rewardKey)
      }
    })
  }

  rewardKeys.push(...extraPointKeys)

  if (data?.basePoint != null && data.basePoint !== '' && Number(data.basePoint) !== 0) {
    rewardKeys.push(`points:${getRewardAmountKey(data.basePoint)}`)
  }

  const matchedKey = rewardKeys.find((key) => rewardImages.some((item) => item?.key === key && item?.image))
  const matchedImage = rewardImages.find((item) => item?.key === matchedKey)

  return matchedImage?.image || ''
}

function getRewardImage(data, i18n) {
  return findConfiguredRewardImage(data, i18n) || i18n?.fallbackIcon || ''
}

function formatPointWithCozyCoins(point, i18n) {
  if (point == null || point === '' || Number(point) === 0) return ''
  return `${point} ${i18n.cozyCoins}`
}

function formatRewardText(template, amount) {
  if (!template) return amount || ''
  if (amount == null || amount === '') return ''

  return template.replace(/\{\{\s*amount\s*\}\}/gi, amount)
}

function formatExtraRewardName(reward, i18n) {
  const amount = reward?.rewardAmount

  if (Number(reward?.rewardType) === 10) {
    return Number(reward?.amountType) === 0
      ? formatRewardText(i18n.rewardCouponPercent, amount)
      : formatRewardText(i18n.rewardCouponAmount, amount)
  }

  if (Number(reward?.rewardType) === 20) return formatRewardText(i18n.rewardGiftCard, amount)
  if (Number(reward?.rewardType) === 40) return formatPointWithCozyCoins(amount, i18n)
  if (Number(reward?.rewardType) === 51) return formatRewardText(i18n.rewardLuckyDraw, amount)

  return amount || ''
}

function getRewardName(data, i18n) {
  const names = []
  const pointText = formatPointWithCozyCoins(data?.basePoint, i18n)

  if (pointText) names.push(pointText)

  if (Array.isArray(data?.extraRewards)) {
    data.extraRewards.forEach((extraReward) => {
      const name = formatExtraRewardName(extraReward, i18n)
      if (name) names.push(name)
    })
  }

  return names.join(' + ')
}

function getLotModalOptions(reward) {
  const i18n = window.__PD26_CHECKIN_I18N__
  const isPrize = isPrizeReward(reward)
  const rewardName = getRewardName(reward, i18n)

  return {
    state: isPrize ? 'prize' : 'coins',
    title: isPrize ? i18n.productTitle : i18n.coinsTitle,
    image: getRewardImage(reward, i18n),
    imageAlt: rewardName,
    strongText: rewardName,
    noteText: isPrize ? i18n.productNote : i18n.coinsNote,
    actionText: isPrize ? i18n.productActionText : i18n.coinsActionText,
  }
}

function openPd26LotModalFromReward(reward) {
  window.openPd26LotModal(getLotModalOptions(reward))
}

async function runPd26SignFlow(trigger) {
  const root = pd26CheckinState.root || document.querySelector('[data-pd26-checkin]')
  if (!root || !trigger) return

  const i18n = window.__PD26_CHECKIN_I18N__

  if (!isLoggedIn()) return

  const todayItem = getTodayItem(pd26CheckinState.activitySignBeanList)
  if (!todayItem) {
    Pd26Toast.show(i18n.toastNotToday)
    return
  }

  if (isSigned(todayItem)) return

  if (pd26CheckinState.signing) return

  const activityCode = getActivityCode()
  if (!activityCode) {
    Pd26Toast.show(i18n.toastSignFailed)
    return
  }

  pd26CheckinState.signing = true
  trigger.disabled = true

  try {
    const signRes = await doSign({
      activityCode,
    })
    openPd26LotModalFromReward(signRes)
    await initCheckin()
  } catch (error) {
    Pd26Toast.show(getApiMessage(error) || i18n.toastSignFailed)
  } finally {
    pd26CheckinState.signing = false
    updateCheckinButton(root, pd26CheckinState.activitySignBeanList)
  }
}

async function handlePd26LotTrigger(trigger) {
  const root = pd26CheckinState.root || document.querySelector('[data-pd26-checkin]')
  if (!root) return

  if (!isLoggedIn()) {
    setPendingSignAfterLogin(true)
    openLoginForCheckin()
    return
  }

  await runPd26SignFlow(trigger)
}

async function resumePendingSignAfterLogin() {
  if (!hasPendingSignAfterLogin()) return
  if (!isLoggedIn()) return
  if (!document.querySelector('[data-pd26-checkin]')) {
    clearPendingSignAfterLogin()
    return
  }

  clearPendingSignAfterLogin()

  const root = pd26CheckinState.root || document.querySelector('[data-pd26-checkin]')
  const trigger = root?.querySelector('[data-pd26-lot-trigger]')
  if (!trigger) return

  await runPd26SignFlow(trigger)
}

async function handlePd26AuthLogin() {
  await initCheckin()
  await resumePendingSignAfterLogin()
}

function handlePd26AuthDismiss() {
  if (!isLoggedIn()) clearPendingSignAfterLogin()
}

/** 后端日期为 UTC-5 业务日历；用 dayjs 格式化为 MM/DD（不做时区插件换算） */
function formatCardDate(dateStr) {
  if (dateStr == null || dateStr === '') return ''
  const s = String(dateStr).trim().replace(/\//g, '-')
  const d = dayjs(s)
  return d.isValid() ? d.format('MM/DD') : ''
}
/** API 枚举：0=是/已签到/特殊日，1=否/未签到/非特殊日 */
function isSigned(item) {
  return Number(item.isSign) === 0
}

function isSpecialDate(item) {
  return Number(item.isSpecialDate) === 0
}

function getCardClasses(item, itemIndex, todayIndex) {
  const classes = ['pd26-checkin-card']

  if (isSigned(item)) {
    classes.push('is-checked')
    return classes
  }

  if (isSpecialDate(item)) {
    classes.push('is-gift')
    return classes
  }

  if (isPastCheckinSlot(itemIndex, todayIndex)) {
    classes.push('is-old')
  }

  return classes
}

function getCardImageSrc(item, options) {
  const { defaultImage, boxFallbackIcon } = options
  return item.continuousIconUrl ? boxFallbackIcon : defaultImage
}

function getCurrentStreakDays(res) {
  return Number(res?.currentStreakDays ?? 0)
}

function getCardLabel(item) {
  if (item.name) return item.name
  if (isSpecialDate(item)) return 'Special Gift'
  if (item.point) return `${item.point} Coins`
  return ''
}

const PD26_GRID_SKELETON_MIN = 7
const PD26_GRID_SKELETON_MAX = 28
const PD26_GRID_SKELETON_DEFAULT = 24

function getGridSkeletonCount(root) {
  const raw = Number(
    root?.dataset?.skeletonCount ??
    window.__PD26_CHECKIN_I18N__?.gridSkeletonCount ??
    PD26_GRID_SKELETON_DEFAULT,
  )

  if (!Number.isFinite(raw)) return PD26_GRID_SKELETON_DEFAULT

  return Math.min(
    PD26_GRID_SKELETON_MAX,
    Math.max(PD26_GRID_SKELETON_MIN, Math.round(raw)),
  )
}

function renderSkeletonCard() {
  return `<div class="pd26-checkin-card pd26-checkin-card--skeleton" aria-hidden="true">
    <span class="pd26-skeleton pd26-skeleton--icon"></span>
    <div class="pd26-copy">
      <span class="pd26-skeleton pd26-skeleton--line"></span>
      <span class="pd26-skeleton pd26-skeleton--line pd26-skeleton--short"></span>
    </div>
  </div>`
}

function renderSkeletonGrid(root, count) {
  const total = count ?? getGridSkeletonCount(root)
  return Array.from({ length: total }, renderSkeletonCard).join('')
}

function setCheckinGridLoading(grid, root, loading) {
  if (!grid) return

  const i18n = window.__PD26_CHECKIN_I18N__ || {}
  grid.classList.toggle('is-loading', loading)
  grid.setAttribute('aria-busy', loading ? 'true' : 'false')
  grid.setAttribute('aria-label', loading ? i18n.gridLoadingLabel : i18n.gridLabel)

  if (loading && !grid.querySelector('.pd26-checkin-card--skeleton')) {
    grid.innerHTML = renderSkeletonGrid(root)
  }
}

function scrollPd26GridToToday(grid) {
  if (!grid) return

  const scroll = () => {
    if (grid.scrollHeight <= grid.clientHeight + 2) return

    const todayEl = grid.querySelector('.pd26-checkin-card[data-pd26-today]')
    if (!todayEl) return

    const gridRect = grid.getBoundingClientRect()
    const todayRect = todayEl.getBoundingClientRect()
    const targetTop = grid.scrollTop + todayRect.top - gridRect.top - ((grid.clientHeight - todayEl.offsetHeight) / 2)

    grid.scrollTop = Math.max(0, targetTop)
  }

  requestAnimationFrame(scroll)
}

function renderCheckinCard(item, options) {
  const { checkedLabel, itemIndex, todayIndex } = options
  const label = getCardLabel(item)
  const dateText = formatCardDate(item.date)
  const className = getCardClasses(item, itemIndex, todayIndex).join(' ')
  const todayAttr = Number(item.isNowDay) === 0 ? ' data-pd26-today' : ''
  const src = getCardImageSrc(item, options)

  const imageHtml = src
    ? `<img src="${src}" alt="${label}" loading="lazy">`
    : ''

  const checkedMarkHtml = isSigned(item)
    ? `<div class="pd26-mark">
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true" focusable="false">
      <use href="#pd26-checked"></use>
    </svg>
    <span>${checkedLabel}</span>
  </div>`
    : ''

  return `<div class="${className}" role="listitem"${todayAttr}>
    ${imageHtml}
    ${checkedMarkHtml}
    <div class="pd26-copy">
      <p class="pd26-name">${label}</p>
      <p class="pd26-date">${dateText}</p>
    </div>
  </div>`
}

function getBonusStreakTemplate(streak, i18n, res) {
  if (Number(res?.totalDays) === 8) return i18n.bonusTotal8
  if (streak === 1) return i18n.bonusStreak1
  if (streak === 2) return i18n.bonusStreak2
  if (streak === 3) return i18n.bonusStreak3
  if (streak === 4) return i18n.bonusStreak4
  if (streak === 5) return i18n.bonusStreak5
  if (streak === 6) return i18n.bonusStreak6
  if (streak >= 7) return i18n.bonusStreak7
  return ''
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function formatBonusStreak(template, rewardName) {
  if (!template) return rewardName || ''
  if (!rewardName) return template

  return template.replace(/\{\{\s*reward\s*\}\}/gi, escapeHtml(rewardName))
}

function getBonusHintText(activitySignBeanList, res) {
  const i18n = window.__PD26_CHECKIN_I18N__

  const todayItem = getTodayItem(activitySignBeanList)
  if (!todayItem) return i18n.bonusUnsigned

  const streak = getCurrentStreakDays(res)
  const template = getBonusStreakTemplate(streak, i18n, res)
  const rewardName = res?.nextRewardDesc?.trim()

  if (template && rewardName) {
    return formatBonusStreak(template, rewardName)
  }

  if (template) return template

  return i18n.bonusUnsigned
}

function getCheckedDaysHtml(days) {
  const i18n = window.__PD26_CHECKIN_I18N__ || {}
  const templates = i18n.checkedDaysHtml || {}
  const key = days === 1 ? 'one' : days === 0 ? 'zero' : 'other'

  return templates[key] || templates.other || templates.zero || ''
}

function updateCheckinSummary(root, activitySignBeanList, res) {
  const streakDays = getCurrentStreakDays(res)
  const summary = root.querySelector('.pd26-line')
  const checkedDaysHtml = getCheckedDaysHtml(streakDays)

  if (summary && checkedDaysHtml) {
    summary.innerHTML = checkedDaysHtml
  }

  const checkedNum = root.querySelector('.pd26-num')
  if (checkedNum) checkedNum.textContent = String(streakDays)

  const bon = root.querySelector('[data-pd26-bon]')
  if (bon) bon.innerHTML = getBonusHintText(activitySignBeanList, res)
}

function updateCheckinButton(root, activitySignBeanList) {
  const btn = root.querySelector('[data-pd26-lot-trigger]')
  if (!btn) return

  if (!isLoggedIn()) {
    btn.classList.remove('is-signed')
    btn.disabled = false
    return
  }

  const todayItem = getTodayItem(activitySignBeanList)
  const signed = todayItem ? isSigned(todayItem) : false

  btn.classList.toggle('is-signed', signed)
  btn.disabled = signed
}

function renderCheckinGrid(root, activitySignBeanList, res) {
  const grid = root.querySelector('[data-pd26-grid]')
  if (!grid) return

  const i18n = window.__PD26_CHECKIN_I18N__
  const options = {
    boxFallbackIcon: i18n.boxFallbackIcon,
    checkedLabel: i18n.checkedLabel,
  }

  const todayIndex = getTodayListIndex(activitySignBeanList)

  grid.classList.remove('is-loading')
  grid.setAttribute('aria-busy', 'false')
  grid.setAttribute('aria-label', i18n.gridLabel)
  grid.innerHTML = activitySignBeanList
    .map((item, index) =>
      renderCheckinCard(item, {
        ...options,
        itemIndex: index,
        todayIndex,
        defaultImage: res.defaultImage,
      }),
    )
    .join('')


  updateCheckinSummary(root, activitySignBeanList, res)
  updateCheckinButton(root, activitySignBeanList)
  scrollPd26GridToToday(grid)
}

async function initCheckin() {
  const root = document.querySelector('[data-pd26-checkin]')
  if (!root) return

  pd26CheckinState.root = root

  const grid = root.querySelector('[data-pd26-grid]')
  const isInitialLoad = grid?.classList.contains('is-loading')
  if (isInitialLoad) setCheckinGridLoading(grid, root, true)

  try {
    const res = await getCheckinList()
    const activitySignBeanList = res?.activitySignBeanList || []
    pd26CheckinState.res = res
    pd26CheckinState.activityCode = res?.activityCode || ''
    pd26CheckinState.activitySignBeanList = activitySignBeanList
    renderCheckinGrid(root, activitySignBeanList, res)
  } catch (error) {
    console.error('[pd26-checkin] init failed', error)
    if (isInitialLoad && grid) setCheckinGridLoading(grid, root, true)
    updateCheckinButton(root, pd26CheckinState.activitySignBeanList)
  }
}

document.addEventListener('LUTE:AUTH:LOGIN', handlePd26AuthLogin)
document.addEventListener('LUTE:AUTH:LOGOUT', () => {
  clearPendingSignAfterLogin()
  initCheckin()
})
document.addEventListener('LUTE:AUTH:CLOSE', handlePd26AuthDismiss)

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-pd26-lot-trigger]')
  if (!trigger) return
  event.preventDefault()
  handlePd26LotTrigger(trigger)
})

async function bootstrapCheckin() {
  await initCheckin()
  await resumePendingSignAfterLogin()
  initPd26RewardTestPanel()
}

function hasPd26TestFlag() {
  try {
    const url = new URL(window.location.href)
    return url.searchParams.has('__test__') || url.hash.includes('__test__')
  } catch (_) {
    return false
  }
}

function getPd26TestRewards() {
  return [
    {
      label: 'Sign-in',
      reward: {
        isSign: 1,
        basePoint: 10,
        msg: 'Check-in successful!',
        activityCode: getActivityCode(),
        extraRewards: [],
      },
    },
    {
      label: 'CozyCoins',
      reward: {
        isSign: 1,
        basePoint: 10,
        msg: 'Congratulations!',
        activityCode: getActivityCode(),
        extraRewards: [
          {
            rewardType: 40,
            rewardAmount: 150,
            amountType: 1,
          },
        ],
      },
    },
    {
      label: 'Gift Card $1',
      reward: {
        isSign: 1,
        basePoint: 10,
        msg: 'Congratulations!',
        activityCode: getActivityCode(),
        extraRewards: [
          {
            rewardType: 20,
            rewardAmount: 1,
            amountType: 1,
          },
        ],
      },
    },
    {
      label: 'Gift Card $2',
      reward: {
        isSign: 1,
        basePoint: 10,
        msg: 'Congratulations!',
        activityCode: getActivityCode(),
        extraRewards: [
          {
            rewardType: 20,
            rewardAmount: 2,
            amountType: 1,
          },
        ],
      },
    },
    {
      label: 'Gift Card $3',
      reward: {
        isSign: 1,
        basePoint: 10,
        msg: 'Congratulations!',
        activityCode: getActivityCode(),
        extraRewards: [
          {
            rewardType: 20,
            rewardAmount: 3,
            amountType: 1,
          },
        ],
      },
    },
    {
      label: 'Gift Card $5',
      reward: {
        isSign: 1,
        basePoint: 10,
        msg: 'Congratulations!',
        activityCode: getActivityCode(),
        extraRewards: [
          {
            rewardType: 20,
            rewardAmount: 5,
            amountType: 1,
          },
        ],
      },
    },
    {
      label: 'Mixed',
      reward: {
        isSign: 1,
        basePoint: 10,
        msg: 'Congratulations!',
        activityCode: getActivityCode(),
        extraRewards: [
          {
            rewardType: 40,
            rewardAmount: 150,
            amountType: 0,
          },
          {
            rewardType: 20,
            rewardAmount: 5,
            amountType: 1,
          },
        ],
      },
    },
  ]
}

function initPd26RewardTestPanel() {
  if (!hasPd26TestFlag() || document.querySelector('[data-pd26-test-panel]')) return

  const panel = document.createElement('div')
  panel.setAttribute('data-pd26-test-panel', '')
  panel.setAttribute('aria-label', 'PD26 reward test panel')
  panel.style.cssText = `
    position: fixed;
    z-index: 2147482999;
    right: 16px;
    bottom: 16px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-width: min(360px, calc(100vw - 32px));
    padding: 12px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
  `

  getPd26TestRewards().forEach((item) => {
    const button = document.createElement('button')
    button.type = 'button'
    button.textContent = item.label
    button.style.cssText = `
      min-height: 36px;
      padding: 0 12px;
      border: 0;
      border-radius: 999px;
      background: #6a2b3a;
      color: #ffffff;
      font: inherit;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
    `
    button.addEventListener('click', () => {
      openPd26LotModalFromReward(item.reward)
    })
    panel.appendChild(button)
  })

  document.body.appendChild(panel)
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrapCheckin)
} else {
  bootstrapCheckin()
}